import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo, faqSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { AffiliateButton } from "../components/Affiliate";
import { VpnCard } from "../components/VpnCard";
import { ComparisonTable } from "../components/ComparisonTable";
import { FaqAccordion } from "../components/FaqAccordion";
import { CtaBanner } from "../components/CtaBanner";
import { VPNS, NORD } from "../data/vpns";
import { FAQS } from "../data/faqs";
import { BEST_FOR } from "../data/bestFor";
import { CheckCircle2 } from "lucide-react";

const METHODOLOGY = [
  {
    h: "Speed Testing",
    p: "We run repeated speed tests against a fixed 1 Gbps base connection across 47 server locations — local, transatlantic, and Asia-Pacific — using WireGuard-class protocols, and report average retention.",
  },
  {
    h: "Streaming Verification",
    p: "Every VPN is tested against Netflix (6 libraries), BBC iPlayer, Disney+, Hulu, Amazon Prime Video, DAZN, and Channel 4 over a 6-week window. We record first-attempt success and retry rates.",
  },
  {
    h: "Privacy & Security Audit",
    p: "We verify encryption standards, run DNS/IPv6 leak tests, force-drop connections to test kill switches, and weigh independent no-log audits (PwC, Deloitte, KPMG) far above marketing claims.",
  },
  {
    h: "Value & Usability",
    p: "We score real 2-year pricing, device limits, app quality across four platforms, and support responsiveness. Affiliate commissions never influence scores — rankings are set before partnerships.",
  },
];

const BestVpns = () => (
  <Layout>
    <Seo
      title="Best VPNs 2026: Editor's Rankings — Top 5 Tested & Compared"
      description="Our definitive 2026 VPN rankings. NordVPN, Surfshark, PureVPN, VeePN and AdGuard VPN tested on speed, streaming, privacy and value — with full comparison table."
      schema={faqSchema(FAQS)}
    />
    <section className="grain relative overflow-hidden bg-[#0F172A]">
      <div className="hero-grid absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <h1 className="max-w-4xl font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
          <MaskedLines lines={["Best VPNs of 2026 —", "Editor's Rankings"]} />
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">
          Five leading VPNs. One identical test suite. Hundreds of hours of testing. These are the definitive
          rankings — updated monthly.
        </p>
        <div className="mt-8">
          <AffiliateButton href={NORD.link} testId="bestvpns-hero-cta" className="!px-8 !py-4">
            Try NordVPN — Our #1 Pick
          </AffiliateButton>
        </div>
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-6 text-sm text-slate-300">
          {["47 server locations tested", "7 streaming services verified", "Updated monthly"].map((t) => (
            <span key={t} className="flex items-center gap-2">
              <CheckCircle2 size={15} className="text-[#4687FF]" /> {t}
            </span>
          ))}
        </div>
      </div>
    </section>

    <Breadcrumbs crumbs={[{ label: "Best VPNs", to: "/best-vpns" }]} />

    {/* Quick verdict */}
    <section className="mx-auto max-w-7xl px-6 pt-10 lg:px-8">
      <Reveal>
        <div className="border-l-4 border-[#4687FF] bg-[#4687FF]/[0.05] p-8" data-testid="quick-verdict-box">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4687FF]">Quick Verdict</p>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-700">
            <strong className="font-display text-[#0F172A]">Our #1 pick is NordVPN — here's why:</strong> it's the
            fastest VPN we tested (95%+ speed retention), the only one with a 100% streaming unblocking record, and
            its no-log policy has been audited twice by PwC and Deloitte. From $3.09/month with a 30-day money-back
            guarantee.
          </p>
        </div>
      </Reveal>
    </section>

    {/* Ranked cards */}
    <section className="mx-auto max-w-7xl space-y-6 px-6 py-20 lg:px-8" data-testid="bestvpns-ranked-cards">
      {VPNS.map((v, i) => (
        <VpnCard key={v.slug} vpn={v} index={i} />
      ))}
    </section>

    {/* Full table */}
    <section className="border-y border-slate-200 bg-slate-50 py-24" data-testid="bestvpns-table-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mb-10">
          <h2 className="font-display text-3xl font-black tracking-tighter text-[#0F172A] sm:text-4xl">
            Full Feature Comparison Table
          </h2>
        </Reveal>
        <Reveal>
          <div className="border border-slate-200 bg-white p-6 sm:p-8">
            <ComparisonTable />
          </div>
        </Reveal>
      </div>
    </section>

    {/* Mini reviews */}
    <section className="mx-auto max-w-4xl px-6 py-24 lg:px-8" data-testid="mini-reviews-section">
      <Reveal className="mb-12">
        <h2 className="font-display text-3xl font-black tracking-tighter text-[#0F172A] sm:text-4xl">
          The Five, In Brief
        </h2>
      </Reveal>
      <div className="space-y-10">
        {VPNS.map((v, i) => (
          <Reveal key={v.slug} delay={i * 0.04}>
            <div className="border-b border-slate-200 pb-10">
              <h3 className="font-display text-xl font-extrabold tracking-tight text-[#0F172A]">
                #{v.rank} {v.name} — {v.score}/10
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.miniReview}</p>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <AffiliateButton
                  href={v.link}
                  testId={`mini-review-cta-${v.slug}`}
                  variant={v.slug === "nordvpn" ? "primary" : "outline"}
                  className="!px-5 !py-2.5 !text-xs"
                >
                  {v.cta}
                </AffiliateButton>
                <Link to={`/reviews/${v.slug}`} className="text-xs font-semibold text-[#4687FF] underline underline-offset-4">
                  Full {v.name} review →
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Best for links */}
    <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-8">
      <Reveal className="mb-8">
        <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">Best VPN For...</h2>
      </Reveal>
      <div className="flex flex-wrap gap-3">
        {BEST_FOR.map((b) => (
          <Link
            key={b.slug}
            to={`/best-vpn-for/${b.slug}`}
            className="border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-[#4687FF] hover:text-[#4687FF]"
          >
            {b.label}
          </Link>
        ))}
      </div>
    </section>

    {/* Methodology */}
    <section className="border-t border-slate-200 bg-slate-50 py-24" data-testid="methodology-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mb-12 max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4687FF]">Methodology</p>
          <h2 className="mt-4 font-display text-3xl font-black tracking-tighter text-[#0F172A] sm:text-4xl">
            How We Test & Rank
          </h2>
        </Reveal>
        <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2">
          {METHODOLOGY.map((m, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="h-full bg-white p-8">
                <span className="font-display text-sm font-black text-[#4687FF]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-lg font-extrabold tracking-tight text-[#0F172A]">{m.h}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{m.p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="mx-auto max-w-4xl px-6 py-24 lg:px-8">
      <Reveal className="mb-10">
        <h2 className="font-display text-3xl font-black tracking-tighter text-[#0F172A] sm:text-4xl">
          Frequently Asked Questions
        </h2>
      </Reveal>
      <Reveal>
        <FaqAccordion faqs={FAQS} />
      </Reveal>
    </section>

    <CtaBanner />
  </Layout>
);

export default BestVpns;
